import { useCallback, useEffect } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { preferencesStore } from "@/stores/preferencesStore"
import useGetPreferences from "./api/use-get-preferences"
import useUpdatePreferences from "./api/use-update-preferences"
import { SystemPreferencesDTO, UpdateSystemPreferencesRequest } from "@/gen/api"

export function usePreferencesManager() {
  const queryClient = useQueryClient()
  const {
    data: serverPreferences,
    isLoading: isLoadingFromServer,
    error: serverError,
  } = useGetPreferences()

  const updateMutation = useUpdatePreferences()

  useEffect(() => {
    if (isLoadingFromServer) {
      preferencesStore.setLoading(true)
    } else {
      preferencesStore.setLoading(false)
    }

    if (serverError) {
      preferencesStore.setError(serverError.message)
    } else if (serverPreferences) {
      preferencesStore.setPreferences(serverPreferences)
    }
  }, [serverPreferences, isLoadingFromServer, serverError])

  useEffect(() => {
    if (updateMutation.isPending) {
      preferencesStore.setUpdating(true)
    } else {
      preferencesStore.setUpdating(false)
    }

    if (updateMutation.error) {
      preferencesStore.setError(updateMutation.error.message)
    }
  }, [updateMutation.isPending, updateMutation.error])

  const updateSinglePreference = useCallback(
    async <K extends keyof SystemPreferencesDTO>(key: K, value: SystemPreferencesDTO[K]) => {
      try {
        const currentPreferences = preferencesStore.preferences
        preferencesStore.updatePreference(key, value)

        const updateRequest: UpdateSystemPreferencesRequest = {
          ...(currentPreferences || {}),
          [key]: value,
        }

        await updateMutation.mutateAsync(updateRequest)

        queryClient.invalidateQueries({ queryKey: ["system-preferences"] })
      } catch (error) {
        preferencesStore.setError(
          error instanceof Error ? error.message : "Failed to update preference",
        )
        throw error
      }
    },
    [updateMutation, queryClient],
  )

  const updateMultiplePreferences = useCallback(
    async (updates: Partial<SystemPreferencesDTO>) => {
      try {
        Object.entries(updates).forEach(([key, value]) => {
          preferencesStore.updatePreference(key as keyof SystemPreferencesDTO, value)
        })

        await updateMutation.mutateAsync(updates as UpdateSystemPreferencesRequest)

        queryClient.invalidateQueries({ queryKey: ["system-preferences"] })
      } catch (error) {
        preferencesStore.setError(
          error instanceof Error ? error.message : "Failed to update preferences",
        )
        throw error
      }
    },
    [updateMutation, queryClient],
  )

  const resetPreferences = useCallback(() => {
    const defaultPreferences: SystemPreferencesDTO = {
      language: "en-AU",
    }
    updateMultiplePreferences(defaultPreferences)
  }, [updateMultiplePreferences])

  return {
    preferences: preferencesStore.preferences,
    isLoading: isLoadingFromServer,
    isUpdating: updateMutation.isPending,
    error: updateMutation.error || serverError,

    updatePreference: updateSinglePreference,
    updateMultiplePreferences,
    resetPreferences,
    clearPreferences: preferencesStore.clearPreferences,
    refetch: () => queryClient.invalidateQueries({ queryKey: ["system-preferences"] }),
  }
}

export function useTimezone() {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  const setTimezone = useCallback(() => Promise.resolve(), [])

  return {
    timezone,
    setTimezone,
  }
}

export function useLanguage() {
  const { preferences, updatePreference } = usePreferencesManager()

  const getLanguage = useCallback(() => {
    if (!preferences?.language) return "en-AU"

    if (preferences.language === "en") return "en-US"

    return preferences.language
  }, [preferences])

  const language = getLanguage()

  const setLanguage = useCallback(
    async (newLanguage: string) => {
      const currentLanguage = getLanguage()
      if (newLanguage === currentLanguage) return Promise.resolve()

      return updatePreference("language", newLanguage)
    },
    [updatePreference, getLanguage],
  )

  return {
    language,
    setLanguage,
  }
}

export function useFormattedDate() {
  const { timezone } = useTimezone()

  const formatDateTime = useCallback(
    (date: Date | string | number, options?: Intl.DateTimeFormatOptions) => {
      const dateObj = typeof date === "object" ? date : new Date(date)

      return dateObj.toLocaleString(undefined, {
        timeZone: timezone,
        ...options,
      })
    },
    [timezone],
  )

  const formatDate = useCallback(
    (date: Date | string | number) => {
      return formatDateTime(date, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    },
    [formatDateTime],
  )

  const formatTime = useCallback(
    (date: Date | string | number) => {
      return formatDateTime(date, {
        hour: "2-digit",
        minute: "2-digit",
      })
    },
    [formatDateTime],
  )

  return {
    formatDateTime,
    formatDate,
    formatTime,
  }
}
